import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Target,
  Eye,
  EyeOff,
  RefreshCw,
  X
} from 'lucide-react';
import axios from 'axios';
import './SelectedMetricsCards.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const SelectedMetricsCards = ({
  selectedBusinessSegment,
  selectedFinancialItems,
  selectedBank,
  selectedCategory,
  selectedPeriod,
  title = "Selected Metrics",
  description = "Financial metrics for selected items",
  onUnselectItem
}) => {
  const { country } = useCountry();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Memoize loadMetricsData to prevent unnecessary re-creation
  const loadMetricsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get financial items data
      const response = await axios.get(`${config.apiUrl}/financial-items`, {
        params: {
          business_segment: selectedBusinessSegment,
          category: selectedCategory,
          bank: selectedBank,
          view_type: 'actual' // Always use actual for financial items list
        },
        headers: getDefaultHeaders(country)
      });

      const financialItems = response.data.financial_items;
      
      // Filter to only selected items
      const selectedItems = financialItems.filter(item => 
        selectedFinancialItems.includes(item.id)
      );

      // Convert to metrics cards
      const metricsData = selectedItems.map(item => {
        // Get current and previous period values
        const currentValue = getCurrentPeriodValue(item);
        const previousValue = getPreviousPeriodValue(item);
        const variance = currentValue - previousValue;
        const variancePercent = previousValue !== 0 ? (variance / Math.abs(previousValue)) * 100 : 0;
        
        // Debug logging
        console.log(`SelectedMetricsCards - Item: ${item.Item}, SelectedPeriod: ${selectedPeriod}, CurrentValue: ${currentValue}, PreviousValue: ${previousValue}`);
        
        let trend = 'neutral';
        if (variance > 0) trend = 'up';
        else if (variance < 0) trend = 'down';

        return {
          id: item.id,
          title: item.Item,
          currentValue,
          previousValue,
          variance,
          variancePercent,
          trend,
          category: item.category,
          bank: item.bank,
          period: selectedPeriod || 'Current',
          isVisible: true
        };
      });

      setMetrics(metricsData);
    } catch (err) {
      console.error('Error loading metrics data:', err);
      setError('Failed to load metrics data');
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessSegment, selectedFinancialItems.join(','), selectedBank, selectedCategory, selectedPeriod, country]);

  // Use ref to track previous selections to avoid unnecessary calls
  const prevSelectionsRef = useRef('');
  
  // Load metrics data when selections change
  useEffect(() => {
    const selectionsKey = `${selectedBusinessSegment}-${selectedFinancialItems.join(',')}-${selectedBank}-${selectedCategory}-${selectedPeriod}`;
    
    if (selectedBusinessSegment && selectedFinancialItems.length > 0) {
      // Only fetch if selections actually changed
      if (prevSelectionsRef.current !== selectionsKey) {
        prevSelectionsRef.current = selectionsKey;
        loadMetricsData();
      }
    } else {
      setMetrics([]);
      prevSelectionsRef.current = '';
    }
  }, [selectedBusinessSegment, selectedFinancialItems.join(','), selectedBank, selectedCategory, selectedPeriod, loadMetricsData]);

  const getCurrentPeriodValue = (item) => {
    if (!selectedPeriod) return 0;
    
    // Map UI period values to actual data column names
    const periodMapping = {
      'q1_2025': 'jfm_2025',  // Q1 2025 -> jfm_2025
      'q2_2025': 'amj_2025',  // Q2 2025 -> amj_2025
      'q3_2025': 'jas_2025',  // Q3 2025 -> jas_2025
      'q4_2025': 'ond_2025',  // Q4 2025 -> ond_2025
      'q1_2024': 'jfm_2024',  // Q1 2024 -> jfm_2024
      'q2_2024': 'amj_2024',  // Q2 2024 -> amj_2024
      'q3_2024': 'jas_2024',  // Q3 2024 -> jas_2024
      'q4_2024': 'ond_2024',  // Q4 2024 -> ond_2024
      'q1_2023': 'jfm_2023',  // Q1 2023 -> jfm_2023
      'q2_2023': 'amj_2023',  // Q2 2023 -> amj_2023
      'q3_2023': 'jas_2023',  // Q3 2023 -> jas_2023
      'q4_2023': 'ond_2023',  // Q4 2023 -> ond_2023
      'h1_2025': 'h1_2025',   // H1 2025 -> h1_2025
      'h2_2025': 'h2_2025',   // H2 2025 -> h2_2025
      'h1_2024': 'h1_2024',   // H1 2024 -> h1_2024
      'h2_2024': 'h2_2024',   // H2 2024 -> h2_2024
      'h1_2023': 'h1_2023',   // H1 2023 -> h1_2023
      'h2_2023': 'h2_2023',   // H2 2023 -> h2_2023
      '9m_2025': '9m_2025',   // 9M 2025 -> 9m_2025
      '9m_2024': '9m_2024',   // 9M 2024 -> 9m_2024
      '9m_2023': '9m_2023',   // 9M 2023 -> 9m_2023
      'fy_2025': 'fy_2025',   // FY 2025 -> fy_2025
      'fy_2024': 'fy_2024',   // FY 2024 -> fy_2024
      'fy_2023': 'fy_2023'    // FY 2023 -> fy_2023
    };
    
    const periodKey = periodMapping[selectedPeriod];
    if (periodKey && item[periodKey] !== undefined && item[periodKey] !== null) {
      return parseFloat(item[periodKey]) || 0;
    }
    return 0;
  };

  const getPreviousPeriodValue = (item) => {
    if (!selectedPeriod) return 0;
    
    // Map current period to its previous period (using UI period values)
    const previousPeriodMapping = {
      'q1_2025': 'q4_2024',  // Q1 2025 -> Q4 2024
      'q2_2025': 'q1_2025',  // Q2 2025 -> Q1 2025
      'q3_2025': 'q2_2025',  // Q3 2025 -> Q2 2025
      'q4_2025': 'q3_2025',  // Q4 2025 -> Q3 2025
      'q1_2024': 'q4_2023',  // Q1 2024 -> Q4 2023
      'q2_2024': 'q1_2024',  // Q2 2024 -> Q1 2024
      'q3_2024': 'q2_2024',  // Q3 2024 -> Q2 2024
      'q4_2024': 'q3_2024',  // Q4 2024 -> Q3 2024
      'q1_2023': 'q4_2022',  // Q1 2023 -> Q4 2022
      'q2_2023': 'q1_2023',  // Q2 2023 -> Q1 2023
      'q3_2023': 'q2_2023',  // Q3 2023 -> Q2 2023
      'q4_2023': 'q3_2023',  // Q4 2023 -> Q3 2023
      'h1_2025': 'h2_2024',  // H1 2025 -> H2 2024
      'h2_2025': 'h1_2025',  // H2 2025 -> H1 2025
      'h1_2024': 'h2_2023',  // H1 2024 -> H2 2023
      'h2_2024': 'h1_2024',  // H2 2024 -> H1 2024
      'h1_2023': 'h2_2022',  // H1 2023 -> H2 2022
      'h2_2023': 'h1_2023',  // H2 2023 -> H1 2023
      '9m_2025': '9m_2024',  // 9M 2025 -> 9M 2024
      '9m_2024': '9m_2023',  // 9M 2024 -> 9M 2023
      '9m_2023': '9m_2022',  // 9M 2023 -> 9M 2022
      'fy_2025': 'fy_2024',  // FY 2025 -> FY 2024
      'fy_2024': 'fy_2023',  // FY 2024 -> FY 2023
      'fy_2023': 'fy_2022'   // FY 2023 -> FY 2022
    };
    
    const previousPeriodKey = previousPeriodMapping[selectedPeriod];
    if (previousPeriodKey) {
      // Convert UI period back to data column name
      const periodMapping = {
        'q1_2025': 'jfm_2025', 'q2_2025': 'amj_2025', 'q3_2025': 'jas_2025', 'q4_2025': 'ond_2025',
        'q1_2024': 'jfm_2024', 'q2_2024': 'amj_2024', 'q3_2024': 'jas_2024', 'q4_2024': 'ond_2024',
        'q1_2023': 'jfm_2023', 'q2_2023': 'amj_2023', 'q3_2023': 'jas_2023', 'q4_2023': 'ond_2023',
        'h1_2025': 'h1_2025', 'h2_2025': 'h2_2025', 'h1_2024': 'h1_2024', 'h2_2024': 'h2_2024',
        'h1_2023': 'h1_2023', 'h2_2023': 'h2_2023', '9m_2025': '9m_2025', '9m_2024': '9m_2024',
        '9m_2023': '9m_2023', 'fy_2025': 'fy_2025', 'fy_2024': 'fy_2024', 'fy_2023': 'fy_2023'
      };
      
      const dataColumnName = periodMapping[previousPeriodKey];
      if (dataColumnName && item[dataColumnName] !== undefined && item[dataColumnName] !== null) {
        return parseFloat(item[dataColumnName]) || 0;
      }
    }
    return 0;
  };

  const formatCurrency = (value) => {
    if (value === 0) return '$0';
    if (Math.abs(value) >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    } else if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const toggleCardVisibility = (cardId) => {
    setMetrics(prev => prev.map(metric => 
      metric.id === cardId ? { ...metric, isVisible: !metric.isVisible } : metric
    ));
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'down':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!selectedBusinessSegment || selectedFinancialItems.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="selected-metrics-container">
        <div className="selected-metrics-loading">
          <RefreshCw className="loading-icon" />
          <span>Loading metrics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="selected-metrics-container">
        <div className="selected-metrics-error">
          <div className="mb-2">{error}</div>
          <button 
            onClick={loadMetricsData}
            className="refresh-button"
          >
            <RefreshCw className="refresh-icon" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="selected-metrics-container">
        <div className="selected-metrics-empty">
          <Target className="empty-icon" />
          <p>No metrics available for selected items</p>
          <p className="text-sm mt-1">Please select financial items to view metrics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="selected-metrics-container">
      <div className="selected-metrics-header">
        <div className="selected-metrics-title">
          <Activity className="title-icon" />
          <h3>{title}</h3>
          <span className="selected-metrics-badge">
            {metrics.length} items
          </span>
        </div>
        <button 
          onClick={loadMetricsData}
          className="refresh-button"
        >
          <RefreshCw className="refresh-icon" />
          <span>Refresh</span>
        </button>
      </div>
      <p className="selected-metrics-subtitle">{description}</p>
      
      <div className="selected-metrics-cards">
        {metrics.map((metric) => (
          <div 
            key={metric.id} 
            className={`metric-card ${!metric.isVisible ? 'opacity-50' : ''}`}
          >
            <div className="metric-card-header">
              <div className="metric-card-title">
                <div className="metric-card-icon">
                  {getTrendIcon(metric.trend)}
                </div>
                <div className="metric-card-content">
                  <div className="metric-card-name">{metric.title}</div>
                  <div className="metric-card-tags">
                    <span className="metric-card-tag">{metric.category}</span>
                    <span className="metric-card-tag">{metric.bank}</span>
                  </div>
                </div>
              </div>
              {onUnselectItem && (
                <button 
                  className="metric-unselect-btn"
                  onClick={() => onUnselectItem(metric.id)}
                  title="Remove this metric"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <div className="metric-values">
              {/* Current Value */}
              <div className="metric-value-row">
                <span className="metric-value-label">Current</span>
                <span className="metric-value-current">
                  {formatCurrency(metric.currentValue)}
                </span>
              </div>

              {/* Previous Value */}
              <div className="metric-value-row">
                <span className="metric-value-label">Previous</span>
                <span className="metric-value-previous">
                  {formatCurrency(metric.previousValue)}
                </span>
              </div>

              {/* Variance */}
              <div className="metric-value-row">
                <span className="metric-value-label">Variance</span>
                <div className="metric-variance">
                  <span className={`metric-variance-value trend-${metric.trend}`}>
                    {formatCurrency(metric.variance)}
                  </span>
                  <span className={`metric-variance-percent trend-${metric.trend}-bg`}>
                    {formatPercentage(metric.variancePercent)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SelectedMetricsCards;